import { useMemo } from 'react';
import {
  checkIsGradient,
  getRgbaFromHex,
  rgbaToHexa,
  getGlobalStyleColor,
  isColorReference,
  resolveColorReference,
  getColor,
} from '@builder4/shared/utils/styleHelpers';
import type { Color, Gradient, RGBA } from '@builder4/shared/types/layout';
import type { StyleColors } from '@builder4/shared/types/styles';

export type ColorMode = 'gradient' | 'solid';

export interface UseColorResult {
  /** Whether the color is a gradient */
  isGradient: boolean;
  /** Whether the color is an RGBA object */
  isRgba: boolean;
  /** Whether the color is a reference string */
  isReference: boolean;
  /** Whether the color is null or undefined */
  isNull: boolean;
  /** The resolved color value (after resolving references) */
  resolvedColor: Color | null;
  /** The gradient object if the color is a gradient */
  gradient: Gradient | undefined;
  /** The RGBA object if the color is RGBA */
  rgba: RGBA | undefined;
  /** The hex string (without #) for the color, empty string for gradients/references */
  hex: string;
  /** The hex string (without #) suitable for ColorField inputValue prop */
  hexForInput: string;
  /** The RGBA object suitable for ColorField, null for gradients/references */
  rgbaForInput: RGBA | null;
  /** The CSS color string (resolved, ready for use in styles) */
  cssColor: string;
  /** The current mode of the color for use in the ColorField mode prop*/
  mode: ColorMode;
  /** The original raw value (before resolution) */
  rawValue: Color | string | null | undefined;
}

/**
 * Comprehensive hook for working with colors in the styles system.
 * Handles RGBA colors, gradients, color references, and null values.
 * Provides all necessary conversions and type information.
 *
 * @param color - The color value (Color | string | null | undefined)
 * @param styleColors - Optional StyleColors for resolving color references
 * @returns Object with all color information and conversions
 *
 * @example
 * ```tsx
 * const fontColor = useColor(bodyData.fontColor, styleColors);
 * // fontColor.isGradient, fontColor.hex, fontColor.rgba, etc.
 * ```
 */
export function useColor(
  color: Color | string | null | undefined,
  styleColors?: StyleColors,
): UseColorResult {
  return useMemo(() => {
    // Handle null/undefined
    if (!color) {
      return {
        isGradient: false,
        isRgba: false,
        isReference: false,
        isNull: true,
        resolvedColor: null,
        gradient: undefined,
        rgba: undefined,
        hex: '',
        hexForInput: '',
        rgbaForInput: null,
        cssColor: '',
        mode: 'solid',
        rawValue: color,
      };
    }

    // Handle color references
    if (isColorReference(color)) {
      const resolved = styleColors ? resolveColorReference(color, styleColors) : undefined;
      if (!resolved) {
        return {
          isGradient: false,
          isRgba: false,
          isReference: true,
          isNull: false,
          resolvedColor: null,
          gradient: undefined,
          rgba: undefined,
          hex: '',
          hexForInput: '',
          rgbaForInput: null,
          cssColor: '',
          mode: 'solid',
          rawValue: color,
        };
      }

      // Resolved reference - check if it's gradient or RGBA
      const isGradient = checkIsGradient(resolved);
      if (isGradient) {
        return {
          isGradient: true,
          isRgba: false,
          isReference: true,
          isNull: false,
          resolvedColor: resolved,
          gradient: resolved,
          rgba: undefined,
          hex: '',
          hexForInput: '',
          rgbaForInput: null,
          cssColor: styleColors ? getGlobalStyleColor(color, styleColors) : '',
          mode: 'gradient',
          rawValue: color,
        };
      } else {
        const rgba = resolved as RGBA;
        const hex = rgbaToHexa(rgba);
        return {
          isGradient: false,
          isRgba: true,
          isReference: true,
          isNull: false,
          resolvedColor: resolved,
          gradient: undefined,
          rgba,
          hex: hex.replace('#', ''),
          hexForInput: hex.replace('#', ''),
          rgbaForInput: rgba,
          cssColor: styleColors ? getGlobalStyleColor(color, styleColors) : '',
          mode: 'solid',
          rawValue: color,
        };
      }
    }

    // Handle string values that aren't references (shouldn't happen in practice, but handle gracefully)
    if (typeof color === 'string') {
      // Try to parse as hex
      const rgba = getRgbaFromHex(color);
      if (rgba) {
        const hex = rgbaToHexa(rgba);
        return {
          isGradient: false,
          isRgba: true,
          isReference: false,
          isNull: false,
          resolvedColor: rgba,
          gradient: undefined,
          rgba,
          hex: hex.replace('#', ''),
          hexForInput: hex.replace('#', ''),
          rgbaForInput: rgba,
          cssColor: styleColors ? getGlobalStyleColor(color, styleColors) : '',
          mode: 'solid',
          rawValue: color,
        };
      }
      // Invalid string
      return {
        isGradient: false,
        isRgba: false,
        isReference: false,
        isNull: false,
        resolvedColor: null,
        gradient: undefined,
        rgba: undefined,
        hex: '',
        hexForInput: '',
        rgbaForInput: null,
        cssColor: '',
        mode: 'solid',
        rawValue: color,
      };
    }

    // Handle Color objects (RGBA or Gradient)
    const isGradient = checkIsGradient(color);
    if (isGradient) {
      return {
        isGradient: true,
        isRgba: false,
        isReference: false,
        isNull: false,
        resolvedColor: color,
        gradient: color,
        rgba: undefined,
        hex: '',
        hexForInput: '',
        rgbaForInput: null,
        cssColor: styleColors ? getGlobalStyleColor(color, styleColors) : getColor(color),
        mode: 'gradient',
        rawValue: color,
      };
    } else {
      const rgba = color as RGBA;
      const hex = rgbaToHexa(rgba);
      return {
        isGradient: false,
        isRgba: true,
        isReference: false,
        isNull: false,
        resolvedColor: rgba,
        gradient: undefined,
        rgba,
        hex: hex.replace('#', ''),
        hexForInput: hex.replace('#', ''),
        rgbaForInput: rgba,
        cssColor: styleColors ? getGlobalStyleColor(rgba, styleColors) : getColor(rgba),
        mode: 'solid',
        rawValue: color,
      };
    }
  }, [color, styleColors]);
}
